import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import { Box } from "@memorang/ui";
import { ModuleActionButton } from "components/ModuleActionButton";
import { ModuleMetadataChips } from "components/ModuleMetadataChips";
import { Card, Text } from "react-native-paper";
import type { EXAM_STATUS } from "types/tag";
import { buttonConfig } from "../constants";
import { getProethicaModuleInteractionState } from "../helpers/proethica-module-list";
import { KnowledgeCheckProgress } from "./KnowledgeCheckProgress";
import { ModuleThumbnail } from "./ModuleThumbnail";
import { ProethicaTestCompletedBadge } from "./ProethicaTestCompletedBadge";

export type ModuleListItemProps = {
	title: string;
	subtitle: string;
	statusLabel?: string;
	status: EXAM_STATUS;
	cta: string;
	thumbnailUrl: string;
	thumbnailSource?: string | number | { uri: string };
	hasAccess?: boolean;
	disabled?: boolean;
	onPress?: () => void;
	loading?: boolean;
	typeLabel?: string;
	numItems?: number;
	duration?: number;
	durationLabel?: string;
	isTest?: boolean;
	testKind?: "pretest" | "posttest";
	icon?: string;
	scorePercent?: number;
};

export const ModuleListItem = ({
	title,
	subtitle,
	status,
	cta,
	thumbnailUrl,
	thumbnailSource,
	hasAccess = true,
	disabled,
	onPress = () => undefined,
	loading,
	duration,
	durationLabel,
	typeLabel,
	isTest,
	testKind,
	icon,
	scorePercent,
}: ModuleListItemProps) => {
	const { isMobile } = useDevice();
	const theme = useAppTheme();

	const isComplete = status === "COMPLETE";
	const { buttonMode, buttonIcon } = buttonConfig[status];
	const finalDurationLabel =
		duration !== undefined ? (durationLabel ?? `${duration} min`) : undefined;
	const metadataChipProps = isTest
		? { typeLabel }
		: { durationLabel: finalDurationLabel };
	const { needsPurchase, isCurriculumLocked } =
		getProethicaModuleInteractionState(hasAccess, disabled, isComplete);
	const showScore = isComplete && scorePercent != null;
	const showTestCompletedBadge = showScore && isTest;

	return (
		<Card
			mode="outlined"
			onPress={isCurriculumLocked ? undefined : onPress}
			style={{
				borderRadius: theme.roundness,
				overflow: "hidden",
				backgroundColor: theme.colors.surface,
				borderColor: theme.dark
					? theme.colors.outline
					: theme.colors.outlineVariant,
				marginHorizontal: isMobile ? 16 : 0,
			}}
		>
			<Box
				flexDirection="row"
				alignItems="center"
				padding={12}
				style={{ gap: 12 }}
			>
				{/* Leading Media / Thumbnail */}
				<ModuleThumbnail
					size={72}
					title={title}
					thumbnailSource={thumbnailSource}
					thumbnailUrl={thumbnailUrl}
					locked={isCurriculumLocked}
					isTest={isTest}
					testKind={testKind}
					icon={icon}
				/>
				{/* Title and Content */}
				<Box flex={1} gap={10}>
					<Box flex={1} gap={4}>
						<Text
							variant="titleSmall"
							style={{
								fontWeight: "bold",
								color: theme.colors.onSurface,
							}}
							numberOfLines={2}
						>
							{title}
						</Text>
						{showTestCompletedBadge ? null : (
							<Text
								variant="bodySmall"
								style={{
									color: theme.colors.onSurfaceVariant,
								}}
								numberOfLines={2}
							>
								{subtitle}
							</Text>
						)}
					</Box>
					{showTestCompletedBadge ? (
						<ProethicaTestCompletedBadge scorePercent={scorePercent} />
					) : showScore ? (
						<KnowledgeCheckProgress scorePercent={scorePercent} />
					) : (
						!(isComplete || isTest) && (
							<ModuleMetadataChips {...metadataChipProps} />
						)
					)}
				</Box>
				{/* Trailing Action */}
				<Box justifyContent="center" paddingLeft={4}>
					<ModuleActionButton
						mode={needsPurchase ? "contained-tonal" : buttonMode}
						icon={needsPurchase ? "lock-outline" : buttonIcon}
						cta={cta}
						loading={loading}
						disabled={needsPurchase ? false : loading || isCurriculumLocked}
						compact={isMobile}
						onPress={onPress}
					/>
				</Box>
			</Box>
		</Card>
	);
};
